import { ComponentProps } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { ContactFragment } from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { countries } from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

interface Props {
  contact: ContactFragment
  onEditContact?: () => void
}

export const ContactsDetailsInfo = ({ contact, onEditContact }: Props) => {
  const selectedCountry = (countryCode: string) => {
    const countryList = countries(i18n.locale)
    return countryList.find((c) => c.value.toLocaleLowerCase() === countryCode.toLocaleLowerCase())
      ?.label
  }

  const generateAddress = (): string => {
    if (!contact.address) {
      return ''
    }

    const { line1, line2, city, postalCode, state, countryCode } = contact.address

    const addressParts = []

    if (line1) {
      addressParts.push(line1)
    }

    if (line2) {
      addressParts.push(line2)
    }

    const cityPostal = [city, postalCode].filter(Boolean).join(', ')
    if (cityPostal) {
      addressParts.push(cityPostal)
    }

    const stateCountry = [state, selectedCountry(countryCode)].filter(Boolean).join(', ')
    if (stateCountry) {
      addressParts.push(stateCountry)
    }

    return addressParts.join(', ')
  }

  const taxId = contact.taxId ?? ''
  const address = generateAddress()

  const details = useUnsafeMemo(() => {
    const items: ComponentProps<typeof DetailsItemsList>['details'] = []

    !!contact.legalName &&
      items.push({
        title: i18n.t('finance.clients.clients_details.overview.details.labels.legal_name'),
        content: {
          type: 'item',
          text: contact.legalName,
          action: {
            type: 'copy',
          },
        },
      })

    !!contact.name && contact.legalName != contact.name
    items.push({
      title: i18n.t('finance.clients.clients_details.overview.details.labels.name'),
      content: {
        type: 'item',
        text: contact.name,
      },
    })

    !!taxId &&
      items.push({
        title: i18n.t('finance.clients.clients_details.overview.details.labels.tax_id'),
        content: {
          type: 'item',
          text: taxId,
          action: {
            type: 'copy',
          },
        },
      })

    !!address &&
      items.push({
        title: i18n.t('finance.clients.clients_details.overview.details.labels.address'),
        content: {
          type: 'item',
          text: address,
          action: {
            type: 'copy',
          },
        },
      })

    return items
  }, [contact, taxId, address])

  // Check if any required fields are missing - check the actual contact properties
  const hasMissingFields = !contact.legalName || !contact.taxId || !address

  return (
    <>
      <DetailsItemsList title={i18n.t('employees.profile.overview.title')} details={details} />
      {hasMissingFields && (
        <Box paddingTop="s12">
          <F0Alert
            variant="warning"
            title={i18n.t('finance.clients.clients_details.overview.missing_fields_alert.title')}
            description={i18n.t(
              'finance.clients.clients_details.overview.missing_fields_alert.description'
            )}
            {...(onEditContact && {
              action: {
                label: i18n.t(
                  'finance.clients.clients_details.overview.missing_fields_alert.add_details_button'
                ),
                onClick: onEditContact,
              },
            })}
          />
        </Box>
      )}
    </>
  )
}
